import React, { useCallback, useState } from "react";
import { Modal } from "antd";

type LightboxTriggerProps = {
  role: "button";
  tabIndex: 0;
  onClick: () => void;
  onKeyDown: (event: React.KeyboardEvent<HTMLDivElement>) => void;
  "aria-haspopup": "dialog";
  "aria-label"?: string;
};

interface LightboxProps {
  renderTrigger: (props: LightboxTriggerProps) => React.ReactNode;
  children: React.ReactNode;
  width?: number;
  bodyStyle?: React.CSSProperties;
  maskStyle?: React.CSSProperties;
  className?: string;
  triggerAriaLabel?: string;
}

export const Lightbox: React.FC<LightboxProps> = ({
  renderTrigger,
  children,
  width = 960,
  bodyStyle,
  maskStyle,
  className,
  triggerAriaLabel,
}) => {
  const [open, setOpen] = useState(false);

  const mergedBodyStyle: React.CSSProperties = {
    padding: 16,
    ...bodyStyle,
  };

  const mergedMaskStyle: React.CSSProperties = {
    backgroundColor: "rgba(0, 0, 0, 0.85)",
    ...maskStyle,
  };

  const handleOpen = useCallback(() => {
    setOpen(true);
  }, []);

  const handleClose = useCallback(() => {
    setOpen(false);
  }, []);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        handleOpen();
      }
    },
    [handleOpen]
  );

  return (
    <>
      {renderTrigger({
        role: "button",
        tabIndex: 0,
        onClick: handleOpen,
        onKeyDown: handleKeyDown,
        "aria-haspopup": "dialog",
        "aria-label": triggerAriaLabel,
      })}

      <Modal
        open={open}
        centered
        destroyOnHidden
        footer={null}
        onCancel={handleClose}
        width={width}
        styles={{ body: mergedBodyStyle, mask: mergedMaskStyle }}
        className={className}
      >
        {children}
      </Modal>
    </>
  );
};
